import React, { useMemo } from 'react';
import { Briefcase, ChevronDown, ChevronRight, ChevronUp, GraduationCap, Mail, Phone, User as UserIcon, History } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatAge, formatDateTime, formatRelativePast } from '../../lib/dateFormat';

export type PersonGridCardPerson = {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: 'student' | 'teacher' | 'customer' | 'both';
  status: 'active' | 'inactive' | 'pending';
  instrument?: string;
  joinDate: string;
  birthDate?: string;
  // Kontexte mit laufendem Vertrag bzw. Auftrag: student, customer, teacher.
  activeContexts?: Array<'student' | 'customer' | 'teacher'>;
  wpUserId?: string;
  subProfiles?: string[];
  roles?: string[];
  parentId?: string;
  parentName?: string;
  isSubprofile?: boolean;
  relationshipText?: string;
  lastHistoryType?: string | null;
  lastHistoryDescription?: string | null;
  lastHistoryAt?: string | null;
};

type Props = {
  person: PersonGridCardPerson;
  typeBadges?: Array<'student' | 'customer' | 'teacher'>;
  managedByName?: string;
  subprofileCount?: number;
  expandableChildren?: Array<{
    id: string;
    name: string;
    relationshipText?: string;
    subprofileIndex: number;
    birthDate?: string;
    /** Läuft für das Profil gerade Unterricht? (T-0042) */
    aktiv?: boolean;
  }>;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenChild?: (subIndex: number) => void;
  onEdit?: () => void;
  onOpenDeepLink?: () => void;
  onEmail?: () => void;
};

function TypeIcon({ type }: { type: PersonGridCardPerson['type'] }) {
  if (type === 'student') return <GraduationCap className="h-4 w-4 text-indigo-500" />;
  if (type === 'teacher') return <Briefcase className="h-4 w-4 text-emerald-500" />;
  if (type === 'customer') return <UserIcon className="h-4 w-4 text-blue-500" />;
  if (type === 'both') {
    return (
      <div className="flex">
        <GraduationCap className="h-4 w-4 text-indigo-500" />
        <Briefcase className="ml-1 h-4 w-4 text-emerald-500" />
      </div>
    );
  }
  return null;
}

export default function PersonGridCard({
  person,
  typeBadges,
  managedByName,
  subprofileCount,
  expandableChildren,
  isExpanded,
  onToggleExpand,
  onOpenChild,
  onEdit,
  onOpenDeepLink,
  onEmail,
}: Props) {
  const formatHistoryType = (type?: string | null): string | null => {
    const key = String(type || '').trim().toLowerCase();
    if (!key) return null;
    const labels: Record<string, string> = {
      note: 'Notiz',
      data_change: 'Datenänderung',
      person_update: 'Person aktualisiert',
      person_delete: 'Person gelöscht',
      role_add: 'Rolle hinzugefügt',
      teacher_update: 'Lehrkraft aktualisiert',
      subprofile_create: 'Schülerprofil erstellt',
      subprofile_update: 'Schülerprofil aktualisiert',
      subprofile_delete: 'Schülerprofil gelöscht',
      contingent_create: 'Kontingent erstellt',
      contingent_update: 'Kontingent aktualisiert',
      contingent_delete: 'Kontingent gelöscht',
      course_change: 'Kursänderung',
      status_change: 'Statusänderung',
      payment: 'Zahlung',
      email: 'E-Mail',
      call: 'Anruf',
      update: 'Update',
    };
    return labels[key] || key.replaceAll('_', ' ');
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const openDetails = () => {
    if (onOpenDeepLink) {
      onOpenDeepLink();
      return;
    }
    onEdit?.();
  };
  const tel = useMemo(() => {
    const phone = (person.phone || '').trim();
    return phone ? `tel:${phone}` : '';
  }, [person.phone]);
  const normalizedBadges = (typeBadges && typeBadges.length > 0
    ? typeBadges
    : (person.type === 'student'
      ? ['student']
      : (person.type === 'teacher' ? ['customer', 'teacher'] : ['customer']))) as Array<'student' | 'customer' | 'teacher'>;
  const isTeacher = normalizedBadges.includes('teacher');
  const isCustomer = normalizedBadges.includes('customer');
  const isStudent = normalizedBadges.includes('student');
  const isSubprofile = person.isSubprofile === true;
  const alter = person.birthDate ? formatAge(person.birthDate) : '';
  const laufendeKontexte = (person.activeContexts || []).filter((kontext) =>
    normalizedBadges.includes(kontext)
  );
  // Die Zahl kommt aus der Liste, die Profile selbst erst beim Ausklappen — der
  // Knopf darf deshalb nicht davon abhaengen, dass sie schon geladen sind.
  const kinderGeladen = (expandableChildren || []).length > 0;
  const hasExpandableChildren = isCustomer && (kinderGeladen || (subprofileCount || 0) > 0);

  return (
    <div
      className={cn(
        'bg-white rounded-2xl shadow-sm border px-4',
        isSubprofile ? 'py-2.5' : 'py-4',
        'grid gap-4 items-start',
        'grid-cols-1 md:grid-cols-[300px_190px_1fr_220px]',
        isTeacher ? 'border-l-4 border-l-emerald-500' : '',
        isSubprofile ? 'border-l-4 border-l-violet-500' : '',
        isCustomer && !isTeacher ? 'border-l-4 border-l-blue-500' : ''
      )}
      role="link"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={(e) => {
        if (e.key === 'Enter') openDetails();
      }}
    >
      {/* Person */}
      <div
        className={cn(
          'min-w-0',
          isSubprofile ? 'space-y-1.5' : 'space-y-2'
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1">
              {normalizedBadges.map((badge) => {
                const badgeClass = cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold',
                  badge === 'teacher' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : '',
                  badge === 'customer' ? 'border-blue-300 text-blue-700 bg-blue-50' : '',
                  badge === 'student' ? 'border-violet-300 text-violet-700 bg-violet-50' : ''
                );
                const label = badge === 'teacher' ? 'Lehrer' : badge === 'customer' ? 'Kunde' : 'Schüler';
                return (
                  <Badge key={badge} variant="outline" className={badgeClass}>
                    <TypeIcon type={badge === 'student' ? 'student' : badge === 'teacher' ? 'teacher' : 'customer'} />
                    <span>{label}</span>
                  </Badge>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <div className="text-sm font-semibold leading-tight truncate">{person.name}</div>
              {alter ? (
                <span className="shrink-0 text-[11px] font-normal text-muted-foreground">
                  ({alter})
                </span>
              ) : null}
              {/* Archiviert ist der Datensatz selbst (PERS-6.1) — unabhängig
                  davon, ob noch ein Vertrag läuft. */}
              {person.status === 'inactive' ? (
                <span
                  data-testid="badge-archiviert"
                  className="shrink-0 inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700"
                >
                  Archiviert
                </span>
              ) : null}
              {/* Statt aktiv/inaktiv: was gerade laeuft, je Kontext benannt.
                  Laeuft nichts, steht auch nichts da. */}
              {laufendeKontexte.map((kontext) => (
                <span
                  key={kontext}
                  className="shrink-0 inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700"
                >
                  {kontext === 'teacher' ? 'Aktive Aufträge'
                    : kontext === 'customer' ? 'Aktive Verträge'
                    : 'Aktiver Vertrag'}
                </span>
              ))}
            </div>
            {/* Erreichbarkeit im Klartext — spart den Weg ueber die Detailseite. */}
            {person.email ? (
              <div className="truncate text-xs text-muted-foreground" title={person.email}>
                {person.email}
              </div>
            ) : null}
            {person.phone ? (
              <div className="truncate text-xs text-muted-foreground">{person.phone}</div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {typeof subprofileCount === 'number' && subprofileCount > 0 ? (
            <Badge variant="outline">{subprofileCount} Zugeordnete Personen</Badge>
          ) : null}
          {hasExpandableChildren ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={(e) => {
                stop(e);
                onToggleExpand?.();
              }}
            >
              {isExpanded ? <ChevronUp className="mr-1.5 h-3.5 w-3.5" /> : <ChevronDown className="mr-1.5 h-3.5 w-3.5" />}
              {isExpanded ? 'Zugeordnete einklappen' : 'Zugeordnete ausklappen'}
            </Button>
          ) : null}
        </div>
        {isStudent && managedByName ? (
          // Beschriftung laut Mockup „sidigo.user.personenverwaltung-ui.html";
          // der Fliesstext der Beschreibung nennt dieselbe Zeile „verwaltet von".
          <div className="text-xs font-medium text-violet-700">Kunde: {managedByName}</div>
        ) : null}
        {isSubprofile && person.relationshipText ? (
          <div className="text-xs text-violet-700">{person.relationshipText}</div>
        ) : null}
      </div>

      {/* Kontakt */}
      <div className={cn('min-w-0', isSubprofile ? 'space-y-1.5' : 'space-y-2')}>
        <div className="text-xs font-semibold text-muted-foreground">Kontakt</div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg"
            disabled={!tel}
            onClick={(e) => {
              stop(e);
              if (tel) window.open(tel);
            }}
            title="Anrufen"
          >
            <Phone className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-10 p-0 rounded-lg"
            disabled={!onEmail}
            onClick={(e) => {
              stop(e);
              onEmail?.();
            }}
            title="E-Mail"
          >
            <Mail className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Historie */}
      <div className={cn('min-w-0', isSubprofile ? 'space-y-1.5' : 'space-y-2')}>
        <div className="text-xs font-semibold text-muted-foreground">Letzte Aktivität</div>
        {person.lastHistoryDescription ? (
          <div className="max-w-full rounded-lg border border-border/70 bg-slate-50/70 px-3 py-2">
            <div className="flex items-start gap-2">
              <History className="mt-0.5 h-3.5 w-3.5 text-slate-500" />
              <div className="min-w-0 w-full">
                <div className="truncate text-xs font-medium text-slate-800" title={person.lastHistoryDescription || ''}>
                  {person.lastHistoryDescription}
                </div>
                <div
                  className="mt-0.5 truncate text-[11px] text-muted-foreground"
                  title={person.lastHistoryAt ? formatDateTime(person.lastHistoryAt) : ''}
                >
                  {formatHistoryType(person.lastHistoryType) ? `${formatHistoryType(person.lastHistoryType)} • ` : ''}
                  {person.lastHistoryAt ? formatRelativePast(person.lastHistoryAt) : '-'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed px-3 py-2 text-xs text-muted-foreground">
            Noch keine Historie
          </div>
        )}
      </div>

      {/* Aktionen */}
      <div className="min-w-0 shrink-0 flex flex-col gap-2">
        <Button
          type="button"
          variant="secondary"
          className={cn('justify-start', isSubprofile ? 'h-9' : '')}
          onClick={(e) => {
            stop(e);
            openDetails();
          }}
        >
          <ChevronRight className="mr-2 h-4 w-4" />
          Zur Person
        </Button>
      </div>

      {hasExpandableChildren && isExpanded ? (
        <div className="md:col-span-4 rounded-xl border border-dashed bg-slate-50/70 px-3 py-2">
          <div className="mb-2 text-xs font-semibold text-muted-foreground">Zugeordnete Schülerprofile</div>
          {!kinderGeladen ? (
            <div className="px-2.5 py-2 text-xs text-muted-foreground">Wird geladen …</div>
          ) : null}
          <div className="space-y-1.5">
            {(expandableChildren || []).map((child) => (
              <button
                key={child.id}
                type="button"
                className="flex w-full items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-left hover:bg-muted/60"
                onClick={(e) => {
                  stop(e);
                  onOpenChild?.(child.subprofileIndex);
                }}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 truncate text-xs font-medium">
                    <span className="truncate">{child.name}</span>
                    {child.aktiv ? (
                      <Badge variant="outline" className="h-4 border-emerald-300 px-1 text-[10px] text-emerald-700">aktiv</Badge>
                    ) : null}
                  </div>
                  {child.relationshipText || child.birthDate ? (
                    <div className="truncate text-[11px] text-muted-foreground">
                      {[child.relationshipText, child.birthDate ? formatAge(child.birthDate) : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  ) : null}
                </div>
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  zum Profil
                  <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}


